import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createBlock as _createBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "c-sidebar__layout" }
const _hoisted_2 = ["aria-current", "aria-controls", "onClick"]
const _hoisted_3 = { class: "c-sidebar__icon-wrap" }
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = { class: "c-sidebar__content" }
const _hoisted_6 = ["id", "aria-label"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Text } from '@sitecore-jss/sitecore-jss-vue';
import type { ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import componentFactory from '@/temp/componentFactory';
import type { SidebarItemFields, SidebarProps } from '@/types/components/Sidebar';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

type SidebarSource = ScItem<SidebarItemFields> & {
  uid?: string;
  rendering?: ComponentRendering;
};

type DynamicComponentItem = ComponentRendering & {
  id?: string;
  template?: string;
  templateName?: string;
};

interface NormalizedSidebarItem extends SidebarSource {
  itemId: string;
  titleField: SidebarItemFields['title'];
  titleValue: string;
  anchorId: string;
  components: DynamicComponentItem[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Sidebar',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;

const activeItemId = ref<string>();
const scrollSyncLocked = ref(false);
const headerOffset = ref(0);
const rootEl = ref<HTMLElement | null>(null);
const sidebarEl = ref<HTMLElement | null>(null);

const fields = computed(() => props.fields || {});
const sourceItems = computed<SidebarSource[]>(() => fields.value.items ?? []);
const rootStyle = computed(() => ({
  ...(utils.overall?.baseComStyle?.(fields.value) || {}),
  '--sidebar-header-offset': `${headerOffset.value}px`,
}));
const rootClass = computed(() => utils.overall.baseComClass(fields.value, 'c-sidebar'));
const activeItem = computed(() => items.value.find((item) => item.itemId === activeItemId.value));

function getFieldValue(field?: string | { value?: string }) {
  if (typeof field === 'string') return field;
  return field?.value ?? '';
}

function stripHtml(value?: string) {
  return `${value ?? ''}`
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function slugify(value?: string) {
  return stripHtml(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getComponentName(componentData?: DynamicComponentItem) {
  return `${
    componentData?.componentName ||
    componentData?.templateName ||
    componentData?.template ||
    'FeaturedResourceCard'
  }`.replace(/\s+/g, '');
}

function resolveDynamicComponent(componentData?: DynamicComponentItem) {
  const componentName = getComponentName(componentData);
  return componentFactory(componentName) || componentName;
}

function getItemKey(item: SidebarSource, index: number) {
  return (
    getFieldValue(item?.fields?.key) ||
    stripHtml(getFieldValue(getTitleField(item))) ||
    item?.id ||
    item?.uid ||
    `sidebar-item-${index + 1}`
  );
}

function getTitleField(item: SidebarSource): SidebarItemFields['title'] {
  return item?.fields?.title ?? { value: item?.displayName ?? item?.name ?? '' };
}

const items = computed<NormalizedSidebarItem[]>(() =>
  sourceItems.value.map((item, index) => {
    const titleField = getTitleField(item);
    const titleValue = stripHtml(getFieldValue(titleField));
    const key = getItemKey(item, index);

    return {
      ...item,
      itemId: `sidebar-item-${slugify(key) || index + 1}`,
      titleField,
      titleValue,
      anchorId: `sidebar-${slugify(key) || index + 1}`,
      components: (item?.fields?.components ?? []).map((componentData) => ({
        ...componentData,
        componentName: getComponentName(componentData),
      })),
    };
  })
);

watch(
  items,
  (list) => {
    if (!list.length) {
      activeItemId.value = undefined;
      return;
    }

    if (!list.some((item) => item.itemId === activeItemId.value)) {
      activeItemId.value = list[0].itemId;
    }
  },
  { immediate: true }
);

function updateHeaderOffset() {
  if (typeof window === 'undefined') return;

  const headerEl =
    window.document.querySelector('#header-content .c-gen-nav') ??
    window.document.querySelector('.c-gen-nav');
  const headerTopEl = headerEl?.querySelector('.c-gen-nav__top');
  const topRect = headerTopEl?.getBoundingClientRect?.();
  headerOffset.value = Math.round(
    topRect?.bottom ?? headerEl?.getBoundingClientRect?.().bottom ?? 0
  );
}

function scrollToItem(item: NormalizedSidebarItem, behavior: ScrollBehavior = 'smooth') {
  const anchorEl = document.getElementById(item.anchorId);
  if (!anchorEl) return;

  const top = window.scrollY + anchorEl.getBoundingClientRect().top - headerOffset.value - 24;
  window.scrollTo({ top, behavior });

  if (window.history?.replaceState) {
    window.history.replaceState(null, '', `#${item.anchorId}`);
  }
}

function syncActiveItemByScroll() {
  if (!items.value.length || scrollSyncLocked.value) return;

  const detectLine = headerOffset.value + 80;
  let nextActive = items.value[0];

  for (const item of items.value) {
    const anchorEl = document.getElementById(item.anchorId);
    if (!anchorEl) continue;

    if (anchorEl.getBoundingClientRect().top <= detectLine) {
      nextActive = item;
    } else {
      break;
    }
  }

  if (nextActive.itemId !== activeItemId.value) {
    activeItemId.value = nextActive.itemId;
  }
}

function unlockScrollSync() {
  scrollSyncLocked.value = false;
}

function onItemClick(item: NormalizedSidebarItem) {
  activeItemId.value = item.itemId;
  scrollSyncLocked.value = true;
  scrollToItem(item);
  window.setTimeout(unlockScrollSync, 900);
}

function isActiveItem(item: NormalizedSidebarItem) {
  return Boolean(activeItemId.value) && activeItemId.value === item.itemId;
}

function getDynamicComponentProps(componentData: DynamicComponentItem) {
  return {
    fields: componentData?.fields ?? {},
    rendering: componentData,
    page: props.page,
  };
}

function getIconSrc(item: NormalizedSidebarItem) {
  return item.fields?.iconImage?.value?.src || '';
}

function getIconAlt(item: NormalizedSidebarItem) {
  return item.fields?.iconImage?.value?.alt || item.titleValue || '';
}

function applyHashItem() {
  if (typeof window === 'undefined') return;

  const hash = decodeURIComponent(window.location.hash.slice(1));
  if (!hash) return;

  const target = items.value.find((item) => item.anchorId === hash);
  if (target) {
    activeItemId.value = target.itemId;
    nextTick(() => scrollToItem(target, 'auto'));
  }
}

let scrollHandler: (() => void) | undefined;
let resizeHandler: (() => void) | undefined;

onMounted(() => {
  nextTick(() => {
    updateHeaderOffset();
    applyHashItem();
    syncActiveItemByScroll();
  });

  scrollHandler = () => {
    updateHeaderOffset();
    syncActiveItemByScroll();
  };
  resizeHandler = () => {
    updateHeaderOffset();
    syncActiveItemByScroll();
  };

  window.addEventListener('scroll', scrollHandler, { passive: true });
  window.addEventListener('resize', resizeHandler);
});

onBeforeUnmount(() => {
  if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
  if (resizeHandler) window.removeEventListener('resize', resizeHandler);
});

return (_ctx: any,_cache: any) => {
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (_openBlock(), _createElementBlock("section", {
    ref_key: "rootEl",
    ref: rootEl,
    class: _normalizeClass(rootClass.value),
    style: _normalizeStyle(rootStyle.value)
  }, [
    _createVNode(_component_ContentGrid, {
      "desktop-default-grid": [2, 20],
      "mobile-default-grid": [1, 14]
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("aside", {
            ref_key: "sidebarEl",
            ref: sidebarEl,
            class: "c-sidebar__nav",
            "aria-label": "Resource sections"
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
              return (_openBlock(), _createElementBlock("button", {
                key: item.itemId,
                class: _normalizeClass(['c-sidebar__nav-item', { active: isActiveItem(item) }]),
                type: "button",
                "aria-current": isActiveItem(item) ? 'true' : undefined,
                "aria-controls": item.anchorId,
                onClick: ($event: any) => (onItemClick(item))
              }, [
                _createElementVNode("span", _hoisted_3, [
                  (getIconSrc(item))
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        class: "c-sidebar__icon-image",
                        src: getIconSrc(item),
                        alt: getIconAlt(item)
                      }, null, 8, _hoisted_4))
                    : (_openBlock(), _createElementBlock("span", {
                        key: 1,
                        class: _normalizeClass([
                  'c-sidebar__icon',
                  `c-sidebar__icon--${item.fields?.icon?.value || 'tube'}`,
                ])
                      }, null, 2))
                ]),
                _createVNode(_unref(Text), {
                  class: "c-sidebar__nav-title",
                  field: item.titleField
                }, null, 8, ["field"]),
                _cache[0] || (_cache[0] = _createElementVNode("span", {
                  class: "c-sidebar__arrow",
                  "aria-hidden": "true"
                }, null, -1))
              ], 10, _hoisted_2))
            }), 128))
          ], 512),
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
              return (_openBlock(), _createElementBlock("section", {
                id: item.anchorId,
                key: item.itemId,
                class: "c-sidebar__panel",
                "aria-label": item.titleValue
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(item.components, (componentData, componentIndex) => {
                  return (_openBlock(), _createBlock(_resolveDynamicComponent(resolveDynamicComponent(componentData)), _mergeProps({ ref_for: true }, getDynamicComponentProps(componentData), {
                    key: componentData?.uid ?? componentData?.id ?? `${item.itemId}-${componentIndex}`
                  }), null, 16))
                }), 128))
              ], 8, _hoisted_6))
            }), 128))
          ])
        ])
      ]),
      _: 1
    })
  ], 6))
}
}

})